// File: src/components/artistDashboard/chartingServices/StartTheBuzzModal.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProfile } from '../../../context/ProfileContext';

// Assets
import tipWhite from '../../../assets/tip_white.png';
import trendsChartingAd from '../../../assets/trendsChartingAd.png';
import radioChartingAd from '../../../assets/radioChartingAd.png';

// Import Confirm Modal
import ConfirmPromotionModal from './ConfirmPromotionModal';

const StartTheBuzzModal = ({ isOpen, onClose, song, isDarkMode }) => {
  const { activeProfile } = useProfile();
  
  // Real artist point balance (with fallback for previewing)
  const availablePoints = activeProfile?.fanPoints ?? activeProfile?.points ?? 75000;

  const basePrice = 75; 
  // Conversion: 1,000 fan points = $1.00 AUD (10 pts = $0.01)
  const maxPointsNeeded = basePrice * 1000; 
  const maxApplicablePoints = Math.min(availablePoints, maxPointsNeeded);

  const [pointsToApply, setPointsToApply] = useState(0);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  if (!isOpen || !song) return null;

  // Real-time calculation of remaining balance
  const discountDollars = pointsToApply / 1000;
  const cashRemaining = Math.max(0, basePrice - discountDollars);

  const handleSliderChange = (e) => {
    const val = Number(e.target.value);
    // Ensure we round to nearest 10 points (1 cent) and clamp within bounds
    const roundedToCent = Math.round(val / 10) * 10;
    setPointsToApply(Math.min(roundedToCent, maxApplicablePoints));
  };

  const fillPercent = maxApplicablePoints > 0 
    ? Math.min(100, Math.max(0, (pointsToApply / maxApplicablePoints) * 100))
    : 0;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-3xl border p-6 sm:p-8 shadow-2xl transition-all ${
              isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-white border-gray-200 text-slate-900'
            }`}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-zinc-200 dark:border-zinc-800 mb-6">
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-brand-pink">
                  Sponsored Campaign
                </span>
                <h3 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-slate-900 dark:text-white mt-0.5">
                  Start The Buzz
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-slate-900 dark:hover:text-white text-2xl font-bold p-1 leading-none"
              >
                &times;
              </button>
            </div>

            <div className="space-y-8">
              {/* TOP SECTION: Trends Mockup (Mobile 1st, Desktop 2nd) + Aspirational Copy */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div className="order-1 md:order-2 flex flex-col items-center justify-center">
                  <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-400 uppercase tracking-wider mb-2">
                    Vertical Trends Swipe Feed
                  </span>
                  <div className="relative w-full max-w-[280px] sm:max-w-[300px] rounded-2xl overflow-hidden shadow-xl border border-gray-200 dark:border-zinc-700/40 bg-gray-100 dark:bg-zinc-900">
                    {song.albumArtworkURL && (
                      <div
                        className="absolute z-0 overflow-hidden"
                        style={{
                          top: '21.5%',
                          left: '38.2%',
                          width: '23.6%',
                          height: '57.8%',
                        }}
                      >
                        <img
                          src={song.albumArtworkURL}
                          alt={song.title}
                          className="w-full h-full object-cover rounded-sm"
                        />
                      </div>
                    )}
                    <img
                      src={trendsChartingAd}
                      alt="Trends Feed Mockup"
                      className="relative z-10 w-full h-auto object-cover pointer-events-none block"
                    />
                  </div>
                </div>

                <div className="order-2 md:order-1 space-y-3 text-left">
                  <span className="text-[10px] font-black uppercase tracking-widest text-brand-pink">
                    Take The Main Stage
                  </span>
                  <h4 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white leading-tight">
                    From Local Artist to Household Contender
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                    Real momentum doesn't happen by chance — it happens when quality music gets placed in front of genuine tastemakers. Launching <span className="font-bold text-slate-900 dark:text-white">"Start The Buzz"</span> isn't just buying ad space; it’s staking your claim as a priority artist on the Atomic network.
                  </p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 leading-relaxed">
                    Position your sound directly alongside charting heavyweights. With full-bleed cover art placement and guaranteed rotation, listeners encounter an artist on the rise who means business.
                  </p>
                </div>
              </div>

              {/* BOTTOM SECTION: Radio Mockup (Left) + What's Included & Adjusted Payment Card (Right) */}
              <div className="pt-6 border-t border-zinc-200 dark:border-zinc-800 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div className="flex flex-col items-center justify-center">
                  <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-400 uppercase tracking-wider mb-2">
                    Radio Now Playing View
                  </span>
                  <div className="relative w-full max-w-[280px] sm:max-w-[300px] rounded-2xl overflow-hidden shadow-xl border border-gray-200 dark:border-zinc-700/40 bg-gray-100 dark:bg-zinc-900">
                    
                    {song.albumArtworkURL && (
                        <div 
                          className="absolute z-0 overflow-hidden"
                            style={{
                            top: '20%',
                            left: '46%',
                            width: '25%',
                            height: '28%',
                            transform: 'rotate(+26deg) skewX(-10deg)',
                            transformOrigin: 'center center',
                            }}
                        >
                        <img
                          src={song.albumArtworkURL}
                          alt={song.title}
                          className="w-full h-full object-cover rounded-md"
                        />
                      </div>
                    )}
                    <img
                      src={radioChartingAd}
                      alt="Radio Mockup Frame"
                      className="relative z-10 w-full h-auto object-cover pointer-events-none block"
                    />
                  </div>
                </div>

                <div className="space-y-4 text-left">
                  {/* Unboxed What's Included */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-zinc-400">
                      What's Included:
                    </h4>
                    <ul className="space-y-2 text-xs text-slate-600 dark:text-zinc-300">
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-500 font-bold leading-none">✓</span>
                        <span className="leading-relaxed">Guaranteed algorithm priority in the active Trends Queue.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-500 font-bold leading-none">✓</span>
                        <span className="leading-relaxed">Boosted impressions to daily raters and listeners.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-500 font-bold leading-none">✓</span>
                        <span className="leading-relaxed">Direct pathway for listeners to tip and download your track.</span>
                      </li>
                    </ul>
                  </div>

                  {/* 🌟 PAYMENT CARD: Fixed-height single-line header with smooth cent-stepping slider */}
                  <div className="p-4 rounded-2xl border bg-zinc-900 text-white border-zinc-900 shadow-lg space-y-3.5">
                    
                    {/* TOP ROW: Title & Points Counter locked onto a single non-wrapping row */}
                    <div className="flex items-center justify-between gap-2 h-5">
                      <span className="text-[11px] font-bold text-white whitespace-nowrap flex-shrink-0">
                        Select Payment Method:
                      </span>
                      
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-zinc-300 whitespace-nowrap flex-shrink-0 tabular-nums">
                        <img src={tipWhite} alt="Points" className="w-3.5 h-3.5 object-contain flex-shrink-0" />
                        <span>
                          {pointsToApply.toLocaleString()} pts / {availablePoints.toLocaleString()} pts
                        </span>
                      </div>
                    </div>

                    {/* SMOOTH SLIDER: White scrub knob with dynamic pink fill */}
                    <div className="relative flex items-center py-1">
                      <input
                        type="range"
                        min="0"
                        max={maxApplicablePoints}
                        step="10"
                        value={pointsToApply}
                        onChange={handleSliderChange}
                        style={{
                          background: `linear-gradient(to right, #ff00cc ${fillPercent}%, #27272a ${fillPercent}%)`
                        }}
                        className="w-full h-1.5 rounded-lg appearance-none cursor-pointer focus:outline-none transition-colors
                          /* Webkit / Chrome / Safari / Edge Thumb */
                          [&::-webkit-slider-thumb]:appearance-none
                          [&::-webkit-slider-thumb]:w-4
                          [&::-webkit-slider-thumb]:h-4
                          [&::-webkit-slider-thumb]:rounded-full
                          [&::-webkit-slider-thumb]:bg-white
                          [&::-webkit-slider-thumb]:border
                          [&::-webkit-slider-thumb]:border-zinc-300
                          [&::-webkit-slider-thumb]:shadow-[0_0_8px_rgba(255,255,255,0.8)]
                          [&::-webkit-slider-thumb]:hover:scale-110
                          [&::-webkit-slider-thumb]:active:scale-95
                          [&::-webkit-slider-thumb]:transition-transform

                          /* Firefox Thumb */
                          [&::-moz-range-thumb]:w-4
                          [&::-moz-range-thumb]:h-4
                          [&::-moz-range-thumb]:rounded-full
                          [&::-moz-range-thumb]:bg-white
                          [&::-moz-range-thumb]:border
                          [&::-moz-range-thumb]:border-zinc-300
                          [&::-moz-range-thumb]:shadow-[0_0_8px_rgba(255,255,255,0.8)]
                          [&::-moz-range-thumb]:hover:scale-110
                          [&::-moz-range-thumb]:active:scale-95
                          [&::-moz-range-thumb]:transition-transform"
                      />
                    </div>

                    {/* TWO BUTTON DISPLAY: Updates live with cent values */}
                    <div className="grid grid-cols-2 gap-2.5 pt-0.5">
                      {/* Blue cash remaining button */}
                      <div className="py-2.5 px-3 rounded-xl text-xs font-black bg-blue-600 border border-blue-500 text-white shadow-md text-center flex items-center justify-center tabular-nums">
                        ${cashRemaining.toFixed(2)} AUD
                      </div>

                      {/* Points applied button */}
                      <div className="py-2.5 px-3 rounded-xl text-xs font-black bg-zinc-800/80 border border-zinc-700 text-zinc-200 text-center flex items-center justify-center gap-1.5 tabular-nums">
                        <img src={tipWhite} alt="Pts" className="w-3.5 h-3.5 object-contain" />
                        <span>{pointsToApply.toLocaleString()} pts</span>
                      </div>
                    </div>
                  </div>

                  {/* Let's Do It Button -> Triggers ConfirmPurchaseModal */}
                  <button
                    type="button"
                    onClick={() => setIsConfirmOpen(true)}
                    className="w-full bg-[#ff00cc] hover:bg-[#ff00cc]/90 text-white font-black text-xs sm:text-sm py-3.5 rounded-2xl shadow-lg shadow-[#ff00cc]/25 active:scale-[0.99] transition-all"
                  >
                    Let's Do It! (${cashRemaining.toFixed(2)} AUD)
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Confirmation & Impact Projection Modal */}
      <ConfirmPromotionModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        song={song}
        serviceName="Start The Buzz"
        basePrice={basePrice}
        pointsApplied={pointsToApply}
        cashDue={cashRemaining}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default StartTheBuzzModal;